import type { MatchPair, MonthDetailResponse, QboTxn, StatementLine } from '@/services/bankRecon'

export type ParsedAccountStatus =
  | { kind: 'none' }
  | { kind: 'uploaded' }
  | { kind: 'exceptions'; exceptionCount: number }
  | { kind: 'clean' }
  | { kind: 'reconciled' }

export type BankReconBucket =
  | 'matched_cleared'
  | 'matched_uncleared'
  | 'statement_only'
  | 'qbo_only_in_window'
  | 'qbo_outstanding'
  | 'qbo_stale'
  | 'overridden'

export const EXCEPTION_BUCKETS: BankReconBucket[] = [
  'matched_uncleared',
  'statement_only',
  'qbo_only_in_window',
]

export const BUCKET_LABELS: Record<BankReconBucket, string> = {
  matched_cleared: 'Matched (cleared)',
  matched_uncleared: 'Matched (uncleared)',
  statement_only: 'Statement only',
  qbo_only_in_window: 'QBO only',
  qbo_outstanding: 'QBO outstanding',
  qbo_stale: 'QBO stale',
  overridden: 'Overridden',
}

export type BankReconRow = {
  rowId: string
  bucket: BankReconBucket
  lineKey?: string
  txnId?: string
  statementDate?: string
  statementDescription?: string
  statementAmount?: string
  qboDate?: string
  qboType?: string
  qboDocNumber?: string
  qboName?: string
  qboMemo?: string
  qboAmount?: string
  status?: string
  overrideId?: number
  note?: string
  url?: string
  overrideKind?: string
}

const EXCEPTIONS_RE = /^exceptions\((\d+)\)$/

export function accountHasBankReconUpload(status: string): boolean {
  return parseAccountStatus(status).kind !== 'none'
}

export function parseAccountStatus(status: string): ParsedAccountStatus {
  const trimmed = status.trim()
  if (trimmed === 'none') return { kind: 'none' }
  if (trimmed === 'uploaded') return { kind: 'uploaded' }
  if (trimmed === 'clean') return { kind: 'clean' }
  if (trimmed === 'reconciled') return { kind: 'reconciled' }
  const match = EXCEPTIONS_RE.exec(trimmed)
  if (match) {
    return { kind: 'exceptions', exceptionCount: Number(match[1]) }
  }
  return { kind: 'none' }
}

function rowFromStatementLine(bucket: BankReconBucket, line: StatementLine): BankReconRow {
  return {
    rowId: `${bucket}:${line.line_key}`,
    bucket,
    lineKey: line.line_key,
    statementDate: line.posted_date,
    statementDescription: line.description,
    statementAmount: line.amount_signed,
    status: bucket === 'statement_only' ? 'Unmatched' : undefined,
  }
}

function rowFromQboTxn(bucket: BankReconBucket, txn: QboTxn): BankReconRow {
  return {
    rowId: `${bucket}:${txn.qbo_line_key || txn.txn_id}`,
    bucket,
    txnId: txn.txn_id,
    qboDate: txn.txn_date,
    qboType: txn.txn_type,
    qboDocNumber: txn.doc_number,
    qboName: txn.name,
    qboMemo: txn.memo,
    qboAmount: txn.amount_signed,
    status: txn.cleared_status ?? undefined,
    url: txn.url,
  }
}

function rowFromMatchPair(bucket: BankReconBucket, pair: MatchPair, index: number): BankReconRow {
  const line = pair.line
  const txn = pair.txn
  const id = `${bucket}:${pair.line_key ?? 'line'}:${pair.qbo_line_key ?? pair.txn_id ?? 'txn'}:${index}`
  return {
    rowId: id,
    bucket,
    lineKey: pair.line_key ?? line?.line_key,
    txnId: pair.txn_id ?? txn?.txn_id,
    statementDate: line?.posted_date,
    statementDescription: line?.description,
    statementAmount: line?.amount_signed,
    qboDate: txn?.txn_date,
    qboType: txn?.txn_type,
    qboDocNumber: txn?.doc_number,
    qboName: txn?.name,
    qboMemo: txn?.memo,
    qboAmount: txn?.amount_signed,
    status: txn?.cleared_status ?? 'Matched',
    url: txn?.url,
  }
}

export function flattenBuckets(detail: MonthDetailResponse): BankReconRow[] {
  const buckets = detail.buckets
  const rows: BankReconRow[] = []

  buckets.matched_cleared.forEach((pair, index) => {
    rows.push(rowFromMatchPair('matched_cleared', pair, index))
  })
  buckets.matched_uncleared.forEach((pair, index) => {
    rows.push(rowFromMatchPair('matched_uncleared', pair, index))
  })
  for (const line of buckets.statement_only) {
    rows.push(rowFromStatementLine('statement_only', line))
  }
  for (const txn of buckets.qbo_only_in_window) {
    rows.push(rowFromQboTxn('qbo_only_in_window', txn))
  }
  for (const txn of buckets.qbo_outstanding) {
    rows.push(rowFromQboTxn('qbo_outstanding', txn))
  }
  buckets.overridden.forEach((item, index) => {
    const line = item.line
    const txn = item.txn
    rows.push({
      rowId: `overridden:${item.override_id ?? index}`,
      bucket: 'overridden',
      lineKey: item.line_key ?? line?.line_key,
      txnId: item.txn_id ?? txn?.txn_id,
      statementDate: line?.posted_date,
      statementDescription: line?.description,
      statementAmount: line?.amount_signed,
      qboDate: txn?.txn_date,
      qboType: txn?.txn_type,
      qboDocNumber: txn?.doc_number,
      qboName: txn?.name,
      qboMemo: txn?.memo,
      qboAmount: txn?.amount_signed,
      status: item.kind,
      overrideId: item.override_id ?? undefined,
      note: item.note,
      url: txn?.url,
      overrideKind: item.kind,
    })
  })

  return rows
}

export type VisibilityOptions = {
  showMatched?: boolean
  showOutstanding?: boolean
}

export function isDefaultVisibleRow(row: BankReconRow, options: VisibilityOptions = {}): boolean {
  if (row.bucket === 'matched_cleared') return Boolean(options.showMatched)
  if (row.bucket === 'qbo_outstanding') return Boolean(options.showOutstanding)
  if (EXCEPTION_BUCKETS.includes(row.bucket) || row.bucket === 'overridden') return true
  return false
}

export function filterVisibleRows(
  rows: BankReconRow[],
  options: VisibilityOptions = {},
): BankReconRow[] {
  return rows.filter((row) => isDefaultVisibleRow(row, options))
}

export function bucketLabel(bucket: BankReconBucket): string {
  return BUCKET_LABELS[bucket]
}
